import React, { useEffect, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import {
  ContractorInterface,
  ContractorResourceTypeEnum,
} from '@vatos-pas/common';
import { CapacityStatus } from './useContractorCapacityStatus';
import { CAPACITY_FIELD_MAP } from './capacityFieldMap';

export interface PreSelectedRecord {
  contractorId: string;
  resourceType: ContractorResourceTypeEnum;
}

export const PageContractorCapacityContext = React.createContext<
  Record<string, CapacityStatus>
>({});

export function usePreSelectedContractorCapacity(
  records: PreSelectedRecord[],
  enabled: boolean,
): { statusMap: Record<string, CapacityStatus>; loading: boolean } {
  const dataProvider = useDataProvider();
  const [statusMap, setStatusMap] = useState<Record<string, CapacityStatus>>(
    {},
  );
  const [loading, setLoading] = useState(false);

  const recordsKey = useMemo(
    () =>
      records
        .filter(r => r.contractorId && r.resourceType)
        .map(r => `${r.contractorId}:${r.resourceType}`)
        .sort()
        .join(','),
    [records],
  );

  useEffect(() => {
    const validRecords = records.filter(r => r.contractorId && r.resourceType);

    if (!enabled || validRecords.length === 0) {
      setStatusMap({});
      return;
    }

    const uniqueIds = [...new Set(validRecords.map(r => r.contractorId))];
    const uniqueResourceTypes = [
      ...new Set(validRecords.map(r => r.resourceType)),
    ];

    // Deduplicate (contractorId, resourceType) pairs
    const pairSet = new Set(
      validRecords.map(r => `${r.contractorId}:${r.resourceType}`),
    );
    const uniquePairs = [...pairSet].map(key => {
      const [contractorId, resourceType] = key.split(':');
      return { contractorId, resourceType: resourceType as ContractorResourceTypeEnum };
    });

    setLoading(true);

    const fetchCapacity = async () => {
      try {
        const contractorResult = await dataProvider.getList('contractor', {
          filter: { 'id||$in': uniqueIds.join(',') },
          pagination: { page: 1, perPage: uniqueIds.length },
          sort: { field: 'name', order: 'ASC' },
        });
        const contractorsById: Record<string, ContractorInterface> = {};
        contractorResult.data.forEach((c: any) => {
          contractorsById[c.id] = c;
        });

        const allocResult = await dataProvider.getList(
          'contractor-allocated-capacity',
          {
            filter: {
              'contractorId||$in': uniqueIds.join(','),
              'resourceType||$in': uniqueResourceTypes.join(','),
            },
            pagination: {
              page: 1,
              perPage: uniqueIds.length * uniqueResourceTypes.length,
            },
            sort: { field: 'contractorId', order: 'ASC' },
          },
        );

        const workloadMap: Record<string, number> = {};
        allocResult.data.forEach((row: any) => {
          workloadMap[`${row.contractorId}:${row.resourceType}`] = Number(
            row.totalAllocated,
          );
        });

        const map: Record<string, CapacityStatus> = {};
        uniquePairs.forEach(({ contractorId, resourceType }) => {
          const contractor = contractorsById[contractorId];
          if (!contractor) return;
          const capacityField = CAPACITY_FIELD_MAP[resourceType];
          if (!capacityField) return;
          const capacity = contractor[capacityField] ?? 0;
          const workload = workloadMap[`${contractorId}:${resourceType}`] ?? 0;
          map[`${contractorId}:${resourceType}`] = {
            isOverCapacity: capacity > 0 && workload >= capacity,
            workload,
            capacity,
          };
        });

        setStatusMap(map);
      } catch (err) {
        console.error('Failed to load pre-selected contractor capacity', err);
        setStatusMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchCapacity();
  }, [enabled, recordsKey]);

  return { statusMap, loading };
}
